import { Card } from '@yleisradio/areena-types';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import styles from './SimpleCard.module.scss';
import { useControl } from 'components/Controls/Control/useControl';
import { useTranslation } from 'hooks/useTranslation';
import { Button } from 'components/Button';
import { useSortable } from '@dnd-kit/sortable';
import DragHandleIcon from 'assets/dragHandle.svg';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import { QueueItemData } from '../types';
import { useEffect } from 'react';
import { useEscHandler } from 'contexts/EscHandlerContext/EscHandlerContext';

interface Props {
  card: Card;
  cardKey: string;
  isCheckboxSelected: boolean;
  isSortable?: boolean;
  setCheckboxSelected: (value: boolean) => void;
  queueKey?: string;
}

export const SimpleCard: React.FC<Props> = ({
  card,
  cardKey,
  isCheckboxSelected,
  setCheckboxSelected,
  isSortable = false,
  queueKey,
}) => {
  const textContentId = sanitizeHtmlId(cardKey + 'text');

  const { controls = [] } = card;
  const playControl = controls.find((c) => c.tag === 'play');

  const { control, action, executingAction } = useControl({
    control: playControl,
  });

  const onClick = !executingAction ? action : undefined;
  const t = useTranslation();

  const textContent = [card.title, card.description].filter(Boolean).join(', ');

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({
    data: { textContent } as QueueItemData,
    disabled: !isSortable,
    id: queueKey || cardKey,
  });

  const {
    addEventHandler: addEscHandler,
    removeEventHandler: removeEscHandler,
  } = useEscHandler();

  useEffect(() => {
    // Prevent queue dialog from closing when sorting with keyboard
    if (isDragging) {
      addEscHandler(() => {}, 'SimpleCard');
      return () => removeEscHandler('SimpleCard');
    }
  }, [addEscHandler, isDragging, removeEscHandler]);

  return (
    <div
      data-testid="card"
      aria-labelledby={textContentId}
      className={styles.container}
      ref={setNodeRef}
      role="group"
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
    >
      <label className={styles.queueCheckBoxLabel}>
        <input
          type="checkbox"
          className={styles.queueCheckBox}
          checked={isCheckboxSelected}
          onChange={(event) => {
            setCheckboxSelected(event.target.checked);
          }}
        />
        <VisuallyHidden>{t('selectQueueItem')}</VisuallyHidden>
      </label>
      <div className={styles.cardTextContentWrapper} id={textContentId}>
        <div className={styles.cardTitle}>
          <Button
            pointer={control?.destination}
            text={card.title || ''}
            disabled={!!control?.disabled}
            onClick={onClick}
            variant="text"
            size="sm"
            padding="none"
            textAlign="left"
          />
        </div>
        <span className={styles.cardDescription}>{card.description}</span>
      </div>
      {isSortable ? (
        <button
          className={classNames(
            styles.handle,
            isDragging && styles.handleDragging
          )}
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
        >
          <DragHandleIcon className={styles.handleIcon} />
          <VisuallyHidden>{t('draggableLabel')}</VisuallyHidden>
        </button>
      ) : (
        <div className={styles.handlePlaceholder} />
      )}
    </div>
  );
};
